// src/components/AdminUpload.js
import React, { useState } from "react";
import { ref, set, push } from "firebase/database";
import { db } from "../services/firebase";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import "./AdminUpload.css";
import { FaCheck, FaCheckCircle } from "react-icons/fa";
import { MdCheckCircle } from "react-icons/md";

function AdminUpload({ user }) {
  const [topic, setTopic] = useState("");
  const [title, setTitle] = useState("");
  const [countdown, setCountdown] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [allowReattempt, setAllowReattempt] = useState(false);
  const [showResultAfterSubmit, setShowResultAfterSubmit] = useState(true);
  const [delayedResultTime, setDelayedResultTime] = useState("");

  const [mcqs, setMcqs] = useState([]);
  const [manualMCQ, setManualMCQ] = useState({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const handleManualChange = (field, value) => {
    setManualMCQ({ ...manualMCQ, [field]: value });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...manualMCQ.options];
    newOptions[index] = value;
    setManualMCQ({ ...manualMCQ, options: newOptions });
  };

  const addManualMCQ = () => {
    if (!manualMCQ.question || manualMCQ.options.some((opt) => !opt) || !manualMCQ.correctAnswer) {
      alert("Please fill all fields.");
      return;
    }
    const newEntry = { ...manualMCQ, date: today };
    setMcqs([...mcqs, newEntry]);
    setManualMCQ({
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      explanation: "",
    });
  };

  const uploadToFirebase = async () => {
    if (!topic || !title) return alert("Enter topic and title.");
    if (mcqs.length === 0) return alert("No MCQs to upload.");

    const quizId = uuidv4();
    const quizData = {
      quizId,
      topic,
      title,
      createdBy: user.uid,
      date: today,
      countdown: countdown || null,
      startTime: startTime || null,
      endTime: endTime || null,
      allowReattempt,
      showResultAfterSubmit,
      delayedResultTime: showResultAfterSubmit ? null : delayedResultTime || null,
      questions: mcqs,
    };

    await set(ref(db, `quizzes/${quizId}`), quizData);

    alert(`✅ Quiz uploaded successfully under '${title}'.`);
    setMcqs([]);
    setTopic("");
    setTitle("");
  };

  const generateWithGemini = async () => {
    if (!topic) return alert("Enter topic first.");
    setLoading(true);
    try {
      const res = await axios.post("https://your-backend-url.com/generate", { topic });
      if (res.data && Array.isArray(res.data.questions)) {
        const formatted = res.data.questions.map((q) => ({ ...q, date: today }));
        setMcqs([...mcqs, ...formatted]);
      } else {
        alert("Invalid response from Gemini.");
      }
    } catch (err) {
      console.error("Gemini API error:", err);
      alert("Failed to generate from Gemini.");
    }
    setLoading(false);
  };

  const handleJSONUpload = (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== "application/json") {
      return alert("Please upload a valid .json file");
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        const extracted = Array.isArray(data) ? data : Object.values(data).flat();
        const valid = extracted.filter(
          (q) => q.question && q.options?.length === 4 && q.correctAnswer && q.explanation
        ).map((q) => ({ ...q, date: today }));
        if (!valid.length) return alert("No valid MCQs found.");
        setMcqs((prev) => [...prev, ...valid]);
        alert(`✅ Loaded ${valid.length} MCQs from JSON.`);
      } catch {
        alert("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };

  const handleTextareaJSON = (e) => {
    try {
      const data = JSON.parse(e.target.value);
      const valid = data.filter(
        (q) => q.question && q.options?.length === 4 && q.correctAnswer && q.explanation
      ).map((q) => ({ ...q, date: today }));
      if (!valid.length) return alert("No valid MCQs found.");
      setMcqs((prev) => [...prev, ...valid]);
      alert(`✅ Pasted ${valid.length} MCQs successfully.`);
    } catch {
      alert("Invalid JSON in textarea.");
    }
  };

  return (
    <div className="admin-panel-wrapper">
      <h2>🛠️ Admin Upload Access</h2>
      <button
        className="submit-btn"
        onClick={() => setShowPanel(!showPanel)}
        style={{ marginBottom: "20px" }}
      >
        {showPanel ? "🙈 Hide Admin Upload  Mcq Panel" : "📂 Show Admin Upload  Mcq Panel"}
      </button>

      {showPanel && (
        <div className="admin-panel">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Quiz Title (e.g., React Dangal)"
            style={{ padding: "10px", width: "100%", marginBottom: "10px" }}
          />

          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter topic (e.g., React Hooks)"
            style={{ padding: "10px", width: "100%", marginBottom: "10px" }}
          />

          <input
            type="number"
            placeholder="Countdown Timer (in minutes)"
            value={countdown}
            onChange={(e) => setCountdown(e.target.value)}
            style={{ padding: "10px", width: "100%", marginBottom: "10px" }}
          />

          <label>Start Time (optional):</label>
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            style={{ padding: "10px", width: "100%", marginBottom: "10px" }}
          />

          <label>End Time (optional):</label>
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            style={{ padding: "10px", width: "100%", marginBottom: "10px" }}
          />

          <label>
            <input
              type="checkbox"
              checked={allowReattempt}
              onChange={(e) => setAllowReattempt(e.target.checked)}
            />
            Allow Reattempt
          </label>

          <br />
          <label>
            <input
              type="checkbox"
              checked={showResultAfterSubmit}
              onChange={(e) => setShowResultAfterSubmit(e.target.checked)}
            />
            Show Result After Submit
          </label>

          {!showResultAfterSubmit && (
            <input
              type="number"
              placeholder="Delay Result By (minutes)"
              value={delayedResultTime}
              onChange={(e) => setDelayedResultTime(e.target.value)}
              style={{ padding: "10px", width: "100%", marginBottom: "10px" }}
            />
          )}

          <button onClick={generateWithGemini} disabled={loading}>
            ⚙️ Generate with BodhAI
          </button>

          <hr />
          <h3>📁 Upload JSON File</h3>
          <input type="file" accept=".json" onChange={handleJSONUpload} />

          <h3>📋 Paste JSON (array format)</h3>
          <textarea
            rows={10}
            cols={80}
            placeholder='[{"question":"...","options":[...],"correctAnswer":"...","explanation":"..."}]'
            onBlur={handleTextareaJSON}
            style={{ width: "100%", padding: "10px", marginBottom: "10px" }}
          />

          <hr />
          <h3>📝 Add MCQ Manually</h3>
          <input
            type="text"
            placeholder="Question"
            value={manualMCQ.question}
            onChange={(e) => handleManualChange("question", e.target.value)}
            style={{ width: "100%", marginBottom: "8px", padding: "6px" }}
          />
          <div className="option-inputs">
            {manualMCQ.options.map((opt, i) => (
              <input
                key={i}
                type="text"
                placeholder={`Option ${i + 1}`}
                value={opt}
                onChange={(e) => handleOptionChange(i, e.target.value)}
                style={{ width: "48%", margin: "4px 1%", padding: "6px" }}
              />
            ))}
          </div>
          <input
            type="text"
            placeholder="Correct Answer"
            value={manualMCQ.correctAnswer}
            onChange={(e) => handleManualChange("correctAnswer", e.target.value)}
            style={{ width: "100%", marginBottom: "8px", padding: "6px" }}
          />
          <input
            type="text"
            placeholder="Explanation"
            value={manualMCQ.explanation}
            onChange={(e) => handleManualChange("explanation", e.target.value)}
            style={{ width: "100%", marginBottom: "8px", padding: "6px" }}
          />
          <button onClick={addManualMCQ}>➕ Add MCQ to Preview</button>

          <hr />
          <button
            onClick={uploadToFirebase}
            disabled={mcqs.length === 0}
            style={{ marginTop: "10px" }}
          >
            ⬆️ Upload All to BodhAI quizzes
          </button>

          <hr />
          <h3>📊 Preview ({mcqs.length} MCQs)</h3>
          {mcqs.length === 0 ? (
            <p>No MCQs added yet.</p>
          ) : (
            <ul className="mcq-preview">
              {mcqs.map((q, i) => (
                <li key={i} style={{ marginBottom: "12px" }}>
                  <strong>Q{i + 1}: {q.question}</strong>
                  <ul>
                    {q.options.map((opt, idx) => (
                      <li key={idx}>
                        {/* {opt} {opt === q.correctAnswer && <strong>✅</strong>} */}
                        {opt} {opt === q.correctAnswer && <strong><FaCheck color="green" size={20}/></strong>}
                      </li>
                    ))}
                  </ul>
                  <em>Explanation: {q.explanation}</em>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminUpload;
